import React from "react";
import { AutoComplete } from "antd";
import type { AutoCompleteProps, GetRef } from "antd";
import useLPDraft from "../useLPDraft";

type AutoCompleteRef = GetRef<typeof AutoComplete>;
export type LPAutoCompleteProps = Omit<AutoCompleteProps<string>, "value" | "defaultValue"> & { value?: string };

const LPAutoComplete = React.memo(
  React.forwardRef<AutoCompleteRef, LPAutoCompleteProps>(function LPAutoComplete(
    { value, onChange, onSelect, onBlur, onInputKeyDown, ...rest },
    ref,
  ) {
    const { draft, change, commit } = useLPDraft(value ?? "");
    const inputRef = React.useRef<AutoCompleteRef>(null);
    const optionRef = React.useRef<Parameters<NonNullable<LPAutoCompleteProps["onChange"]>>[1]>();

    React.useImperativeHandle(ref, () => inputRef.current!);

    const commitValue = () => commit((nextValue) => onChange?.(nextValue, optionRef.current));

    return (
      <AutoComplete
        {...rest}
        ref={inputRef}
        value={draft}
        onChange={(nextValue, option) => {
          optionRef.current = option;
          change(nextValue ?? "");
        }}
        onSelect={(nextValue, option) => {
          optionRef.current = option;
          change(nextValue);
          commitValue();
          onSelect?.(nextValue, option);
        }}
        onBlur={(event) => {
          commitValue();
          onBlur?.(event);
        }}
        onInputKeyDown={(event) => {
          onInputKeyDown?.(event);
          if (event.key === "Enter" && !event.defaultPrevented && !event.nativeEvent.isComposing) {
            // Let AntD finish keyboard option selection before committing on blur.
            queueMicrotask(() => inputRef.current?.blur());
          }
        }}
      />
    );
  }),
);

export default LPAutoComplete;
